// v3E.3 — Appointments page (CRUD + status pills + reminder offsets preview).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, CalendarDays, Clock, MapPin, Trash2, Edit3, Stethoscope, FlaskConical, Heart, Eye, Smile } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { useFamily } from "../contexts/FamilyContext";

const TYPE_META = {
    doctor_consult: { label: "Doctor consult", Icon: Stethoscope, tint: "from-violet-500 to-indigo-500" },
    lab_test:       { label: "Lab test",        Icon: FlaskConical, tint: "from-blue-500 to-cyan-500" },
    followup:       { label: "Follow-up",       Icon: Heart, tint: "from-rose-500 to-pink-500" },
    checkup:        { label: "Check-up",        Icon: Heart, tint: "from-emerald-500 to-teal-500" },
    dental:         { label: "Dental",          Icon: Smile, tint: "from-amber-500 to-orange-500" },
    eye_checkup:    { label: "Eye check-up",    Icon: Eye, tint: "from-fuchsia-500 to-pink-500" },
};

const STATUS_CHIP = {
    scheduled: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
    upcoming:  "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
    completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    cancelled: "bg-zinc-200 text-zinc-700 dark:bg-white/10 dark:text-zinc-300",
    missed:    "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

const EMPTY_FORM = {
    type: "doctor_consult",
    doctor_name: "",
    hospital_clinic_name: "",
    specialty: "",
    date_time: "",
    duration_minutes: 30,
    location: "",
    address: "",
    notes: "",
};

const toLocalDateTimeInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const formatWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const AppointmentForm = ({ initial, familyMemberId, onSubmit, onCancel, submitting }) => {
    const [form, setForm] = useState(() => ({
        ...EMPTY_FORM,
        ...initial,
        date_time: toLocalDateTimeInput(initial?.date_time) || "",
        duration_minutes: initial?.duration_minutes ?? 30,
    }));
    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));
    const submit = (e) => {
        e.preventDefault();
        const dt = new Date(form.date_time);
        if (Number.isNaN(dt.getTime())) { toast.error("Invalid date/time"); return; }
        onSubmit({
            ...form,
            date_time: dt.toISOString(),
            family_member_id: familyMemberId || null,
        });
    };
    return (
        <form onSubmit={submit} className="space-y-3 rounded-3xl border border-brand-100 bg-brand-50/40 p-5 dark:border-brand-400/20 dark:bg-brand-500/5" data-testid="appt-form">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Type</span>
                    <select name="type" value={form.type} onChange={onChange} className="input-clean mt-1" data-testid="appt-type">
                        {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Doctor name</span>
                    <input name="doctor_name" value={form.doctor_name} onChange={onChange} placeholder="Dr. ..." className="input-clean mt-1" data-testid="appt-doctor" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Hospital / clinic</span>
                    <input name="hospital_clinic_name" value={form.hospital_clinic_name} onChange={onChange} placeholder="Apollo, Practo..." className="input-clean mt-1" data-testid="appt-hospital" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Specialty</span>
                    <input name="specialty" value={form.specialty} onChange={onChange} placeholder="Cardiology, GP..." className="input-clean mt-1" data-testid="appt-specialty" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Date & time</span>
                    <input name="date_time" type="datetime-local" value={form.date_time} onChange={onChange} required className="input-clean mt-1" data-testid="appt-when" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Duration (minutes)</span>
                    <input name="duration_minutes" type="number" min="5" max="240" value={form.duration_minutes} onChange={onChange} className="input-clean mt-1" data-testid="appt-duration" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Location / room</span>
                    <input name="location" value={form.location} onChange={onChange} placeholder="OPD-3, Online…" className="input-clean mt-1" data-testid="appt-location" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Address (optional)</span>
                    <input name="address" value={form.address} onChange={onChange} className="input-clean mt-1" data-testid="appt-address" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Notes</span>
                    <textarea name="notes" value={form.notes} onChange={onChange} rows={2} maxLength={500} className="input-clean mt-1" data-testid="appt-notes" />
                </label>
            </div>
            <p className="rounded-xl bg-zinc-50 px-3 py-2 text-[11px] text-zinc-500 dark:bg-white/[0.04] dark:text-zinc-400">
                We'll auto-remind you <strong>1 day before</strong>, <strong>2 hours before</strong> and <strong>30 minutes before</strong>.
            </p>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="appt-cancel">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="appt-save">{submitting ? "Saving..." : initial?.id ? "Update appointment" : "Save appointment"}</button>
            </div>
        </form>
    );
};

const Appointments = () => {
    const { activeMember } = useFamily();
    const fmId = activeMember && !activeMember.is_self ? activeMember.id : null;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (fmId) params.family_member_id = fmId;
            const { data } = await api.get("/appointments", { params });
            setItems(data.items || []);
        } finally { setLoading(false); }
    }, [fmId]);

    useEffect(() => { load(); }, [load]);

    const onCreate = async (payload) => {
        try {
            setSubmitting(true);
            await api.post("/appointments", payload);
            toast.success("Appointment scheduled");
            setAdding(false);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not save");
        } finally { setSubmitting(false); }
    };

    const onUpdate = async (payload) => {
        try {
            setSubmitting(true);
            await api.patch(`/appointments/${editing.id}`, payload);
            toast.success("Appointment updated");
            setEditing(null);
            await load();
        } catch { toast.error("Update failed"); }
        finally { setSubmitting(false); }
    };

    const onDelete = async (it) => {
        if (!window.confirm("Cancel this appointment?")) return;
        try {
            await api.delete(`/appointments/${it.id}`);
            toast.success("Appointment removed");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const onMarkDone = async (it) => {
        try {
            await api.patch(`/appointments/${it.id}`, { status: "completed" });
            toast.success("Marked completed");
            await load();
        } catch { toast.error("Could not update"); }
    };

    const counts = useMemo(() => {
        const c = { all: items.length, upcoming: 0, scheduled: 0, completed: 0, cancelled: 0, missed: 0 };
        items.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
        return c;
    }, [items]);

    const visible = filter === "all" ? items : items.filter((x) => x.status === filter);

    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
                <AuthGuard>
                    <header className="mb-6 flex flex-wrap items-end justify-between gap-3" data-testid="appointments-page">
                        <div>
                            <p className="label-uppercase">Appointments</p>
                            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">All your medical visits, in one place</h1>
                            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Doctor consults you book on PILLS auto-appear here. Add your in-person visits too.</p>
                        </div>
                        {!adding && !editing && (
                            <button onClick={() => setAdding(true)} className="btn-brand !py-2 !px-4 !text-sm" data-testid="appt-add-btn">
                                <Plus className="h-4 w-4" /> New appointment
                            </button>
                        )}
                    </header>

                    <div className="mb-6 flex flex-wrap gap-2" data-testid="appt-filters">
                        {[
                            { v: "all",       label: "All" },
                            { v: "upcoming",  label: "Upcoming" },
                            { v: "scheduled", label: "Scheduled" },
                            { v: "completed", label: "Completed" },
                            { v: "missed",    label: "Missed" },
                            { v: "cancelled", label: "Cancelled" },
                        ].map((f) => (
                            <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`appt-filter-${f.v}`}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition ${filter === f.v ? "bg-brand-700 text-white" : "border border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-white/5"}`}>
                                {f.label} <span className="ml-1 text-[10px] opacity-70">{counts[f.v] || 0}</span>
                            </button>
                        ))}
                    </div>

                    {(adding || editing) && (
                        <div className="mb-6">
                            <AppointmentForm
                                initial={editing}
                                familyMemberId={fmId}
                                onSubmit={editing ? onUpdate : onCreate}
                                onCancel={() => { setAdding(false); setEditing(null); }}
                                submitting={submitting}
                            />
                        </div>
                    )}

                    {loading ? (
                        <div className="grid gap-3 md:grid-cols-2">
                            {[1, 2].map((i) => <div key={i} className="h-40 skeleton rounded-2xl" />)}
                        </div>
                    ) : visible.length === 0 ? (
                        <EmptyState
                            title={items.length === 0 ? "No appointments yet" : `Nothing matches "${filter}"`}
                            description={items.length === 0 ? "Add your first in-person visit, or book a doctor consult on PILLS." : "Try another filter."}
                            action={items.length === 0 ? <button onClick={() => setAdding(true)} className="btn-brand"><Plus className="h-4 w-4" /> Add appointment</button> : null}
                        />
                    ) : (
                        <div className="grid gap-3 md:grid-cols-2" data-testid="appt-list">
                            {visible.map((a) => {
                                const meta = TYPE_META[a.type] || TYPE_META.doctor_consult;
                                const Icon = meta.Icon;
                                return (
                                    <div key={a.id} data-testid={`appt-card-${a.id}`} className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft transition hover:border-brand-200 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <span className={`grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br ${meta.tint} text-white`}>
                                                    <Icon className="h-5 w-5" />
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{a.doctor_name || meta.label}</p>
                                                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{a.hospital_clinic_name || "—"}{a.specialty ? ` · ${a.specialty}` : ""}</p>
                                                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300">
                                                        <Clock className="h-3 w-3" /> {formatWhen(a.date_time)}
                                                    </p>
                                                    {a.location && (
                                                        <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                                                            <MapPin className="h-3 w-3" /> {a.location}
                                                        </p>
                                                    )}
                                                    {a.notes && <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{a.notes}</p>}
                                                </div>
                                            </div>
                                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CHIP[a.status] || STATUS_CHIP.scheduled}`}>{a.status}</span>
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {a.status !== "completed" && a.status !== "cancelled" && (
                                                <button onClick={() => onMarkDone(a)} data-testid={`appt-done-${a.id}`} className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25">Mark done</button>
                                            )}
                                            {!a.auto_created && (
                                                <button onClick={() => { setEditing(a); setAdding(false); }} data-testid={`appt-edit-${a.id}`} className="rounded-full border border-zinc-200 px-3 py-1 text-xs text-zinc-600 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-white/5">
                                                    <Edit3 className="mr-1 inline-block h-3 w-3" /> Edit
                                                </button>
                                            )}
                                            <button onClick={() => onDelete(a)} data-testid={`appt-delete-${a.id}`} className="rounded-full border border-zinc-200 px-3 py-1 text-xs text-zinc-500 hover:border-rose-200 hover:text-rose-600 dark:border-white/10 dark:text-zinc-400 dark:hover:border-rose-400/30">
                                                <Trash2 className="mr-1 inline-block h-3 w-3" /> Cancel
                                            </button>
                                            {a.auto_created && (
                                                <span className="rounded-full bg-zinc-50 px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500 dark:bg-white/[0.04] dark:text-zinc-400">Auto-created</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </AuthGuard>
            </section>
        </Layout>
    );
};

export default Appointments;
